import React, { useState, useEffect } from "react";
import { Box, Button, CircularProgress, Modal, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DropDown from "../baseComponents/dropdown";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import apiClient from "../../axiosClient";
import Picklist from "../../interfaces/picklist";
import MonthYearSelector from "../baseComponents/monthYearSelector";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { decodedJWT, getOwnerName, getOwnerType } from "../../utils/commonUtils";
import RoleManagement from "../../security/roleManagement";

interface BulkDownloadModalProps {
    modalVisible: boolean;
    hideModal: () => void;
}

const styles = {
    modal: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '30%',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        p: 2,
        overflowY: 'auto'
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2
    },
    box: {
        width: '50%',
    },
};

const BulkDownloadModal: React.FC<BulkDownloadModalProps> = ({ modalVisible, hideModal }) => {
    const clients = useSelector((state: RootState) => state.clients.clients);
    const token: JWTTokenPayload|false = decodedJWT();
    const ownerType = getOwnerType(token);
    const ownerName = getOwnerName(token);
    const picklist: Picklist = useSelector((state: RootState) => state.picklists.picklists);
    const statuses = picklist.STATUS.join(',');
    const [isBulkDownloading, setIsBulkDownloading] = useState<boolean>(false);
    const [clientName, setClientName] = useState<string>('');
    const [poolMonth, setPoolMonth] = useState<string>('');
    const [selectedLoanType, setSelectedLoanType] = useState<string>('');
    const [loanTypes, setLoanTypes] = useState<string[]>([]);

    // Initialize loan types
    useEffect(() => {
        if (picklist?.LOAN_TYPE) {
            const availableLoanTypes = picklist.LOAN_TYPE.filter(loanType => loanType.trim() !== '');
            setLoanTypes(availableLoanTypes);
        }
    }, [picklist]);

    const closeModal = () => {
        setClientName('');
        setPoolMonth('');
        setSelectedLoanType('');
        hideModal();
    };

    const bulkDownloadCSV = async () => {
        try {
            if (!clientName && RoleManagement.isAgency(ownerType)) {
                alert('Please select a client!');
                return;
            }
            if (!selectedLoanType) {
                alert('Please select a loan type!');
                return;
            }
            setIsBulkDownloading(true);
            const finalClientName = RoleManagement.isAgency(ownerType) ? clientName : ownerName;
            const apiUrl = `/api/v1/case/bulk_download?pool_month=${poolMonth}&status=${statuses}&client_name=${finalClientName}&loan_type=${selectedLoanType}`;
            const response = await apiClient.get(apiUrl, {
                responseType: "blob"
            });
            if (response.status === 200) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'bulk_download.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            alert('Bulk downloading error: ' + error);
        } finally {
            setIsBulkDownloading(false);
        }
    };

    return (
        <Modal open={modalVisible} onClose={hideModal}>
            <Box sx={styles.modal}>
                <Box sx={styles.header}>
                    <Typography>Bulk Download CSV</Typography>
                    <Button variant="outlined" onClick={closeModal}>
                        <CloseIcon />
                    </Button>
                </Box>
                <Box sx={styles.header}>
                    {RoleManagement.isAgency(ownerType) ? (
                        <Box sx={styles.box}>
                            <DropDown label="Select Client" selectedValue={clientName} items={clients} onSelection={setClientName} keyProp="client_name" valueProp="client_name" displayProp="client_name" />
                        </Box>
                    ) : null}   
                    <MonthYearSelector keys={poolMonth} onDateSelection={setPoolMonth} />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <DropDown 
                        label="Select Loan Type" 
                        selectedValue={selectedLoanType} 
                        items={loanTypes} 
                        onSelection={setSelectedLoanType}
                    />
                </Box>
                <Button variant="contained" fullWidth onClick={bulkDownloadCSV} disabled={isBulkDownloading}>
                    {isBulkDownloading ? (
                        <CircularProgress color="inherit" />
                    ) : (
                        <Typography>Download</Typography>
                    )}
                </Button>
            </Box>
        </Modal>
    );
};

export default BulkDownloadModal;
